import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Truck, MapPin, ArrowLeft, Trash2, Edit3, Upload, Sparkles, Loader2, CheckCircle2, MessageSquare, FileBadge2, RefreshCw, FileSpreadsheet, ShieldCheck, X, Route, ChevronDown, Images, History } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import MultiLoadImportModal from '../components/MultiLoadImportModal';
import ImportHistoryModal from '../components/ImportHistoryModal';
import DashboardLayout from '../components/DashboardLayout';
import RoutePreviewMap from '../components/RoutePreviewMap';
import LoadStopsMap from '../components/LoadStopsMap';
import TripPlaybackModal from '../components/TripPlaybackModal';
import ShareEtaButton from '../components/ShareEtaButton';
import LoadChatDrawer from '../components/LoadChatDrawer';
import LoadEditModal from '../components/LoadEditModal';
import LoadDocumentsModal from '../components/LoadDocumentsModal';
import { MissingDocsBadge } from '../components/MissingDocsChecklist';
import LoadReviewModal from '../components/LoadReviewModal';
import { track } from '../lib/telemetry';
import BulkLoadImportModal from '../components/BulkLoadImportModal';
import CarrierCheckWidget from '../components/CarrierCheckWidget';
import LinkDriverModal from '../components/LinkDriverModal';
import { LoadStatusPill } from '../components/LoadStatusPill';
import { LOAD_STATUSES, statusLabel } from '../lib/loadStatus';
import { Link2Off } from 'lucide-react';
import SearchableDriverSelect from '../components/SearchableDriverSelect';
import ProfitabilityPanel, { ProfitabilityPill, BookingScoreChip } from '../components/ProfitabilityPanel';
import { toast } from '../components/ui/sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const FACTORING_COMPANIES = [
  'Amazon Relay',
  'OTR Solutions',
  'RTS Financial',
  'Apex Capital',
  'TBS Factoring',
  'TAFS',
  'Triumph Business Capital',
  'Internal / Self-funded',
  'Other',
];

const PAY_TYPE_OPTIONS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'cent_per_mile', label: 'Cent Per Mile' },
  { value: 'owner_operator', label: 'Owner Operator' },
];

const LoadsManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loads, setLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [chatLoad, setChatLoad] = useState(null); // { id, load_ref } | null
  const [editLoad, setEditLoad] = useState(null);
  const [linkLoad, setLinkLoad] = useState(null);
  const [docsLoad, setDocsLoad] = useState(null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [showReview, setShowReview] = useState(false);
  const [reviewLoadId, setReviewLoadId] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showMultiImport, setShowMultiImport] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [multiImportFile, setMultiImportFile] = useState(null);
  const [showCarrierCheck, setShowCarrierCheck] = useState(false);
  const [stopsLoad, setStopsLoad] = useState(null);
  const [playbackLoad, setPlaybackLoad] = useState(null);
  const highlightId = searchParams.get('highlight');

  useEffect(() => {
    const q = searchParams.get('status');
    if (q) setStatusFilter(q);
  }, [searchParams]);

  const [form, setForm] = useState({
    // legacy / required
    pickup_location: '',
    delivery_location: '',
    driver_id: '',
    co_driver_id: '',
    pickup_time: '',
    delivery_time: '',
    cargo_description: '',
    weight: '',
    distance: '',
    notes: '',
    // new extended fields
    load_ref: '',
    load_rate: '',
    broker: '',
    pickup_date: '',
    delivery_date: '',
    pickup_city: '',
    pickup_state: '',
    delivery_city: '',
    delivery_state: '',
    // Multi-stop arrays (new)
    pickup_stops: [{ city: '', state: '' }],
    delivery_stops: [{ city: '', state: '' }],
    deadhead_miles: '',
    loaded_miles: '',
    total_miles: '',
    driver_pay_type: 'percentage',
    dispatcher_id: '',
    load_status: 'assigned',
    factoring_company: '',
    file_attachment: null,
    // Phase-1 profitability inputs
    load_type: 'dry_van',
    tolls: '',
    other_costs: '',
    truck_asset_id: '',
  });

  const [trucks, setTrucks] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [fileAttachmentMeta, setFileAttachmentMeta] = useState(null); // {name, sizeKb}

  // PDF extract state
  const fileInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState('');
  const [extractedFields, setExtractedFields] = useState(null);
  const [extractedDriverName, setExtractedDriverName] = useState(null);

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(pdf|png|jpe?g|webp)$/i.test(file.name)) {
      setExtractError('Please upload a PDF, JPG or PNG file.');
      return;
    }
    setExtracting(true);
    setExtractError('');
    setExtractedFields(null);
    setExtractedDriverName(null);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await axios.post(`${API}/loads/extract-pdf`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const ex = res.data?.extracted || {};
      const matched = res.data?.matched_driver_id || '';
      setForm((prev) => ({
        ...prev,
        // legacy combined locations
        pickup_location: ex.pickup_location || prev.pickup_location,
        delivery_location: ex.delivery_location || prev.delivery_location,
        // legacy datetime-ish
        pickup_time: ex.pickup_time || prev.pickup_time,
        delivery_time: ex.delivery_time || prev.delivery_time,
        cargo_description: ex.cargo_description || prev.cargo_description,
        weight: ex.weight || prev.weight,
        distance: ex.distance || ex.total_miles || prev.distance,
        notes: ex.notes
          ? prev.notes ? `${prev.notes}\n${ex.notes}` : ex.notes
          : prev.notes,
        driver_id: matched || prev.driver_id,
        // new extended fields
        load_ref: ex.load_ref || prev.load_ref,
        load_rate: ex.load_rate || ex.rate || prev.load_rate,
        broker: ex.broker || prev.broker,
        factoring_company: ex.factoring_company || prev.factoring_company,
        pickup_date: ex.pickup_date || prev.pickup_date,
        delivery_date: ex.delivery_date || prev.delivery_date,
        pickup_city: ex.pickup_city || prev.pickup_city,
        pickup_state: ex.pickup_state || prev.pickup_state,
        delivery_city: ex.delivery_city || prev.delivery_city,
        delivery_state: ex.delivery_state || prev.delivery_state,
        // Stops: populate first pickup + first delivery from extracted city/state
        pickup_stops: ex.pickup_city || ex.pickup_state
          ? [{ city: ex.pickup_city || '', state: (ex.pickup_state || '').toUpperCase() }]
          : prev.pickup_stops,
        delivery_stops: ex.delivery_city || ex.delivery_state
          ? [{ city: ex.delivery_city || '', state: (ex.delivery_state || '').toUpperCase() }]
          : prev.delivery_stops,
        deadhead_miles: ex.deadhead_miles || prev.deadhead_miles,
        loaded_miles: ex.loaded_miles || prev.loaded_miles,
        total_miles: ex.total_miles || prev.total_miles,
      }));
      const populated = Object.entries(ex).filter(([, v]) => v).map(([k]) => k);
      setExtractedFields(populated);
      if (ex.driver_name && !matched) {
        setExtractedDriverName(ex.driver_name);
      }
    } catch (err) {
      setExtractError(err.response?.data?.detail || 'Failed to extract data from PDF');
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAttachmentSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('Attachment too large (max 10MB).');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result || '').toString().split(',')[1] || '';
      setForm((prev) => ({
        ...prev,
        file_attachment: { name: file.name, mime: file.type, data_b64: base64 },
      }));
      setFileAttachmentMeta({ name: file.name, sizeKb: Math.round(file.size / 1024) });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAttachment = () => {
    setForm((prev) => ({ ...prev, file_attachment: null }));
    setFileAttachmentMeta(null);
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
  };

  // Auto-compute total_miles when deadhead + loaded change
  useEffect(() => {
    const a = parseFloat(form.deadhead_miles);
    const b = parseFloat(form.loaded_miles);
    if (!Number.isNaN(a) && !Number.isNaN(b)) {
      const sum = String(a + b);
      if (form.total_miles !== sum) {
        setForm((prev) => ({ ...prev, total_miles: sum }));
      }
    } else if (!Number.isNaN(b) && (form.deadhead_miles === '' || form.deadhead_miles == null)) {
      if (form.total_miles !== form.loaded_miles) {
        setForm((prev) => ({ ...prev, total_miles: prev.loaded_miles }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.deadhead_miles, form.loaded_miles]);

  // ---- Live profitability preview ----
  const [profitPreview, setProfitPreview] = useState(null);  // { metrics, negotiation_message }
  const [profitPreviewLoading, setProfitPreviewLoading] = useState(false);
  const previewKey = useMemo(() => `${form.load_rate}|${form.total_miles}|${form.loaded_miles}|${form.deadhead_miles}|${form.load_type}|${form.tolls}|${form.other_costs}|${form.driver_pay_type}`, [form.load_rate, form.total_miles, form.loaded_miles, form.deadhead_miles, form.load_type, form.tolls, form.other_costs, form.driver_pay_type]);
  useEffect(() => {
    const rate = parseFloat(String(form.load_rate || '').replace(/[$,\s]/g, ''));
    const miles = parseFloat(String(form.total_miles || form.loaded_miles || ''));
    if (!rate || !miles || rate <= 0 || miles <= 0) {
      setProfitPreview(null);
      return;
    }
    const ctrl = new AbortController();
    const handle = setTimeout(async () => {
      setProfitPreviewLoading(true);
      try {
        const { data } = await axios.post(`${API}/profitability/preview`, {
          load_rate: form.load_rate,
          total_miles: form.total_miles,
          loaded_miles: form.loaded_miles,
          deadhead_miles: form.deadhead_miles,
          tolls: form.tolls ? parseFloat(form.tolls) : null,
          other_costs: form.other_costs ? parseFloat(form.other_costs) : null,
          load_type: form.load_type || 'dry_van',
          driver_pay_type: form.driver_pay_type,
        }, { withCredentials: true, signal: ctrl.signal });
        setProfitPreview(data);
      } catch (e) {
        if (!ctrl.signal.aborted) setProfitPreview(null);
      } finally {
        if (!ctrl.signal.aborted) setProfitPreviewLoading(false);
      }
    }, 350);
    return () => { ctrl.abort(); clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  // ---- Broker history flag (Phase 3) ----
  const [brokerHistory, setBrokerHistory] = useState(null);
  useEffect(() => {
    const broker = (form.broker || '').trim();
    if (broker.length < 2) { setBrokerHistory(null); return; }
    const ctrl = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/profitability/broker-history`, {
          params: { broker, limit: 3 },
          withCredentials: true,
          signal: ctrl.signal,
        });
        if (data?.load_count_total > 0) setBrokerHistory(data);
        else setBrokerHistory(null);
      } catch {
        if (!ctrl.signal.aborted) setBrokerHistory(null);
      }
    }, 450);
    return () => { ctrl.abort(); clearTimeout(handle); };
  }, [form.broker]);

  // ---- Multi-stop helpers ----
  const addPickupStop = () => setForm((p) => ({ ...p, pickup_stops: [...(p.pickup_stops || []), { city: '', state: '' }] }));
  const addDeliveryStop = () => setForm((p) => ({ ...p, delivery_stops: [...(p.delivery_stops || []), { city: '', state: '' }] }));
  const updatePickupStop = (i, field, value) => setForm((p) => {
    const next = [...(p.pickup_stops || [])];
    next[i] = { ...next[i], [field]: field === 'state' ? value.toUpperCase() : value };
    return { ...p, pickup_stops: next };
  });
  const updateDeliveryStop = (i, field, value) => setForm((p) => {
    const next = [...(p.delivery_stops || [])];
    next[i] = { ...next[i], [field]: field === 'state' ? value.toUpperCase() : value };
    return { ...p, delivery_stops: next };
  });
  const removePickupStop = (i) => setForm((p) => {
    const next = (p.pickup_stops || []).filter((_, idx) => idx !== i);
    return { ...p, pickup_stops: next.length ? next : [{ city: '', state: '' }] };
  });
  const removeDeliveryStop = (i) => setForm((p) => {
    const next = (p.delivery_stops || []).filter((_, idx) => idx !== i);
    return { ...p, delivery_stops: next.length ? next : [{ city: '', state: '' }] };
  });

  // ---- Auto-calculate Loaded Miles from stops via backend (debounced) ----
  const [milesCalcState, setMilesCalcState] = useState({ status: 'idle', message: '' });
  const [orderedStopsWithCoords, setOrderedStopsWithCoords] = useState([]);
  const [milesBreakdown, setMilesBreakdown] = useState([]);
  useEffect(() => {
    const pickups = (form.pickup_stops || []).filter((s) => s.city && s.state);
    const deliveries = (form.delivery_stops || []).filter((s) => s.city && s.state);
    if (pickups.length === 0 || deliveries.length === 0) {
      setMilesCalcState({ status: 'idle', message: '' });
      setOrderedStopsWithCoords([]);
      return;
    }
    const t = setTimeout(async () => {
      setMilesCalcState({ status: 'loading', message: 'Calculating route miles…' });
      try {
        const { data } = await axios.post(
          `${API}/load-utilities/calculate-miles`,
          { pickup_stops: pickups, delivery_stops: deliveries },
          { withCredentials: true },
        );
        if (Array.isArray(data?.ordered_stops)) {
          setOrderedStopsWithCoords(data.ordered_stops);
        }
        if (Array.isArray(data?.breakdown)) {
          setMilesBreakdown(data.breakdown);
        }
        if (data?.loaded_miles != null) {
          const m = Math.round(data.loaded_miles);
          setForm((prev) => ({ ...prev, loaded_miles: String(m) }));
          if ((data.unresolved || []).length > 0) {
            setMilesCalcState({ status: 'warn', message: `Calculated ${m} mi — some stops couldn't be geocoded.` });
          } else {
            setMilesCalcState({ status: 'ok', message: `Auto-calculated: ${m} mi across ${(data.breakdown || []).length} legs` });
          }
        } else {
          setMilesCalcState({ status: 'idle', message: '' });
        }
      } catch (_err) {
        setMilesCalcState({ status: 'error', message: 'Could not auto-calculate miles' });
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.pickup_stops), JSON.stringify(form.delivery_stops)]);

  // ---- Auto-calculate Deadhead Miles when driver + first pickup change ----
  const [deadheadCalcState, setDeadheadCalcState] = useState({ status: 'idle', message: '' });
  useEffect(() => {
    if (!form.driver_id) {
      setDeadheadCalcState({ status: 'idle', message: '' });
      return;
    }
    const firstStop = (form.pickup_stops || []).find((s) => s.city && s.state);
    if (!firstStop) {
      setDeadheadCalcState({ status: 'idle', message: '' });
      return;
    }
    const t = setTimeout(async () => {
      setDeadheadCalcState({ status: 'loading', message: 'Looking up last delivery…' });
      try {
        const { data } = await axios.post(
          `${API}/load-utilities/calculate-deadhead`,
          { driver_id: form.driver_id, first_pickup: firstStop },
          { withCredentials: true },
        );
        if (data?.deadhead_miles == null) {
          setDeadheadCalcState({ status: 'warn', message: 'Could not auto-calc deadhead — please enter manually.' });
          return;
        }
        const m = Math.round(data.deadhead_miles);
        setForm((prev) => ({ ...prev, deadhead_miles: String(m) }));
        if (data.source === 'no_previous_load') {
          setDeadheadCalcState({ status: 'ok', message: 'No previous delivered load — deadhead set to 0.' });
        } else if (data.source === 'previous_delivery' && data.from_city) {
          setDeadheadCalcState({ status: 'ok', message: `From last delivery: ${data.from_city}, ${data.from_state} → ${m} mi` });
        } else {
          setDeadheadCalcState({ status: 'ok', message: `Auto-calculated: ${m} mi` });
        }
      } catch (_err) {
        setDeadheadCalcState({ status: 'error', message: 'Deadhead lookup failed' });
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.driver_id, JSON.stringify((form.pickup_stops || [])[0] || {})]);

  // ---- Auto-select Driver Pay Type from driver's profile ----
  const [payTypeAutoNote, setPayTypeAutoNote] = useState('');
  useEffect(() => {
    if (!form.driver_id) {
      setPayTypeAutoNote('');
      return;
    }
    const driver = drivers.find((d) => d.user_id === form.driver_id);
    if (!driver) return;
    let nextType = form.driver_pay_type;
    let note = '';
    if (driver.driver_type === 'owner_operator') {
      nextType = 'owner_operator';
      note = `Auto-selected from driver type: Owner Operator`;
    } else if (driver.pay_type === 'per_mile') {
      nextType = 'cent_per_mile';
      note = `Auto-selected from driver pay profile: Per-Mile`;
    } else if (driver.pay_type === 'percentage') {
      nextType = 'percentage';
      note = `Auto-selected from driver pay profile: Percentage`;
    } else if (driver.pay_type === 'salary' || driver.pay_type === 'hourly') {
      nextType = 'percentage';
      note = `Driver is on ${driver.pay_type} pay — per-load pay type may not apply.`;
    } else {
      note = 'Driver has no pay profile yet — defaulting to Percentage.';
    }
    if (nextType !== form.driver_pay_type) {
      setForm((prev) => ({ ...prev, driver_pay_type: nextType }));
    }
    setPayTypeAutoNote(note);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.driver_id, drivers]);

  // Rate benchmark — debounced fetch when state pair changes
  const [benchmark, setBenchmark] = useState(null);
  useEffect(() => {
    const firstP = (form.pickup_stops || []).find((s) => s.city && s.state);
    const lastD = [...(form.delivery_stops || [])].reverse().find((s) => s.city && s.state);
    const ps = firstP?.state || form.pickup_state;
    const ds = lastD?.state || form.delivery_state;
    if (!ps || !ds) {
      setBenchmark(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${API}/load-benchmarks/rate?pickup_state=${ps}&delivery_state=${ds}`,
          { withCredentials: true },
        );
        setBenchmark(data);
      } catch (_err) {
        setBenchmark(null);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.pickup_stops), JSON.stringify(form.delivery_stops), form.pickup_state, form.delivery_state]);

  // Parse rate (e.g. "$2,800" -> 2800) and total miles
  const parsedRate = (() => {
    const s = String(form.load_rate || '').replace(/[$,\s]/g, '');
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : n;
  })();
  const parsedMiles = (() => {
    const n = parseFloat(form.total_miles);
    return Number.isNaN(n) || n <= 0 ? null : n;
  })();
  const currentPerMile = parsedRate != null && parsedMiles != null ? parsedRate / parsedMiles : null;

  // Compare to benchmark for color coding
  const benchmarkAvg = benchmark?.avg_per_mile ?? benchmark?.company_overall_avg ?? null;
  const benchmarkLabel = benchmark?.avg_per_mile != null
    ? `${benchmark.lane} lane avg`
    : (benchmark?.company_overall_avg != null ? 'company avg' : null);
  let benchmarkTone = 'neutral';
  let benchmarkPctText = null;
  if (currentPerMile != null && benchmarkAvg != null && benchmarkAvg > 0) {
    const pct = ((currentPerMile - benchmarkAvg) / benchmarkAvg) * 100;
    benchmarkPctText = `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
    if (pct >= 5) benchmarkTone = 'good';
    else if (pct <= -10) benchmarkTone = 'bad';
    else benchmarkTone = 'warn';
  }
  const toneStyles = {
    good: 'bg-[#34C759]/15 text-[#117A33] border-[#34C759]/40',
    warn: 'bg-[#FF9500]/15 text-[#B25E00] border-[#FF9500]/40',
    bad: 'bg-red-50 text-red-700 border-red-200',
    neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [loadsRes, driversRes, dispatchersRes] = await Promise.all([
        axios.get(`${API}/loads`, { withCredentials: true }),
        axios.get(`${API}/drivers`, { withCredentials: true }),
        axios.get(`${API}/users/dispatchers`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setLoads(loadsRes.data);
      setDrivers(driversRes.data.filter((d) => d.status === 'active'));
      setDispatchers(dispatchersRes.data || []);
      axios.get(`${API}/assets?asset_type=truck&status=active`, { withCredentials: true })
        .then((r) => setTrucks(r.data || [])).catch(() => setTrucks([]));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.driver_id) {
      setError('Please select a driver');
      return;
    }
    if (!form.load_ref) {
      setError('Load ID is required');
      return;
    }
    if (!form.broker) {
      setError('Broker is required');
      return;
    }
    const firstPickup = (form.pickup_stops || []).find((s) => s.city && s.state);
    const firstDelivery = (form.delivery_stops || []).find((s) => s.city && s.state);
    const missing = [];
    if (!firstPickup && !form.pickup_city) missing.push('Pick Up Stop (city + state)');
    if (!firstDelivery && !form.delivery_city) missing.push('Delivery Stop (city + state)');
    if (!form.pickup_date) missing.push('Pick Up Date');
    if (!form.delivery_date) missing.push('Delivery Date');
    if (!form.loaded_miles) missing.push('Loaded Miles');
    if (!form.total_miles) missing.push('Total Miles');
    if (missing.length) {
      setError(`Please complete the following before importing: ${missing.join(', ')}.`);
      return;
    }
    try {
      // Derive legacy pickup_city/state + delivery_city/state from stops (back-compat)
      const firstP = (form.pickup_stops || []).find((s) => s.city || s.state);
      const lastD = [...(form.delivery_stops || [])].reverse().find((s) => s.city || s.state);
      const payload = {
        ...form,
        pickup_city: form.pickup_city || firstP?.city || '',
        pickup_state: form.pickup_state || firstP?.state || '',
        delivery_city: form.delivery_city || lastD?.city || '',
        delivery_state: form.delivery_state || lastD?.state || '',
        pickup_stops: (form.pickup_stops || []).filter((s) => s.city || s.state),
        delivery_stops: (form.delivery_stops || []).filter((s) => s.city || s.state),
      };
      // Numeric fields must be a number or omitted — empty strings fail backend validation.
      ['tolls', 'other_costs'].forEach((k) => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) {
          delete payload[k];
        } else {
          payload[k] = Number(payload[k]);
        }
      });
      await axios.post(`${API}/loads`, payload, { withCredentials: true });
      setForm({
        pickup_location: '',
        delivery_location: '',
        driver_id: '',
        co_driver_id: '',
        pickup_time: '',
        delivery_time: '',
        cargo_description: '',
        weight: '',
        distance: '',
        notes: '',
        load_ref: '',
        load_rate: '',
        broker: '',
        pickup_date: '',
        delivery_date: '',
        pickup_city: '',
        pickup_state: '',
        delivery_city: '',
        delivery_state: '',
        pickup_stops: [{ city: '', state: '' }],
        delivery_stops: [{ city: '', state: '' }],
        deadhead_miles: '',
        loaded_miles: '',
        total_miles: '',
        driver_pay_type: 'percentage',
        dispatcher_id: '',
        load_status: 'assigned',
        factoring_company: '',
        file_attachment: null,
        truck_asset_id: '',
      });
      setFileAttachmentMeta(null);
      setShowForm(false);
      setExtractedFields(null);
      setExtractedDriverName(null);
      setExtractError('');
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Failed to create load';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        msg = detail
          .map((d) => {
            const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : '';
            return field ? `${field}: ${d.msg}` : d.msg;
          })
          .join('; ');
      }
      setError(msg);
    }
  };

  const handleDelete = async (loadId) => {
    if (!window.confirm('Delete this load?')) return;
    try {
      await axios.delete(`${API}/loads/${loadId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to delete');
    }
  };

  const handleReassign = async (loadId) => {
    const driverId = window.prompt('Enter new driver user_id to reassign:');
    if (!driverId) return;
    try {
      await axios.post(`${API}/loads/${loadId}/assign`, { driver_id: driverId }, { withCredentials: true });
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to reassign');
    }
  };

  const getDriverName = (driverId) => {
    const d = drivers.find((x) => x.user_id === driverId);
    return d ? d.name : driverId;
  };

  if (loading) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }

  const unlinkedCount = loads.filter((l) => l.driver_unlinked).length;
  const filteredLoads = statusFilter === 'all' ? loads
    : statusFilter === 'unlinked' ? loads.filter((l) => l.driver_unlinked)
      : loads.filter((l) => l.status === statusFilter);
  const canLinkDriver = ['company_admin', 'dispatcher', 'super_admin'].includes(user?.role);
  const DriverCell = ({ load, variant = '' }) => load.driver_unlinked ? (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span data-testid={`unlinked-badge-${variant}${load.id}`} title="Imported driver name not linked to a registered driver"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-200 text-slate-700">
        <Link2Off className="w-3 h-3" /> Unlinked
      </span>
      <span className="text-sm text-slate-800 italic" data-testid={`unlinked-name-${variant}${load.id}`}>{load.unmatched_driver_name}</span>
      {canLinkDriver && (
        <button type="button" data-testid={`link-driver-${variant}${load.id}`} onClick={() => setLinkLoad(load)}
          className="text-[11px] font-semibold text-[#002FA7] hover:underline">Link driver</button>
      )}
    </span>
  ) : <>{getDriverName(load.driver_id)}</>;

  const stats = {
    total: loads.length,
    assigned: loads.filter((l) => l.status === 'assigned').length,
    in_transit: loads.filter((l) => l.status === 'in_transit').length,
    delivered: loads.filter((l) => l.status === 'delivered').length,
  };

  return (
    <DashboardLayout>
      <div className="p-8">
        <button
          data-testid="back-to-dashboard-button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Back to Dashboard
        </button>

        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Load Management</h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Create and manage loads. Assign to active drivers.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="vet-carrier-button"
              onClick={() => setShowCarrierCheck((v) => !v)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border-2 border-slate-300 text-slate-700 px-3 sm:px-4 py-2 rounded-md font-semibold hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-sm"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              title="Look up a broker's carrier on FMCSA before assigning"
            >
              <ShieldCheck className="w-4 h-4" strokeWidth={2} />
              <span className="hidden sm:inline">Vet Carrier</span>
              <span className="sm:hidden">Vet</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="import-loads-button"
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border-2 border-[#002FA7] text-[#002FA7] px-3 sm:px-4 py-2 rounded-md font-semibold hover:bg-[#002FA7] hover:text-white transition-all duration-200 text-sm"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  title="Import loads from a rate confirmation, load list, or spreadsheet"
                >
                  <Upload className="w-4 h-4" strokeWidth={2} />
                  <span className="hidden sm:inline">Import Loads</span>
                  <span className="sm:hidden">Import</span>
                  <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80" data-testid="import-loads-menu">
                <DropdownMenuItem data-testid="import-rate-con-item" onClick={() => setShowReview(true)} className="flex items-start gap-3 py-2.5 cursor-pointer">
                  <Sparkles className="w-4 h-4 mt-0.5 text-[#002FA7]" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Rate Confirmation</p>
                    <p className="text-xs text-slate-500">One load · PDF, JPG or PNG from Amazon or any broker · full AI review</p>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="import-load-list-item" onClick={() => { setMultiImportFile(null); setShowMultiImport(true); }} className="flex items-start gap-3 py-2.5 cursor-pointer">
                  <Images className="w-4 h-4 mt-0.5 text-[#002FA7]" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Load List / Screenshot</p>
                    <p className="text-xs text-slate-500">Many loads · Amazon Relay trip list or broker list · PDF, JPG, PNG, CSV · driver match &amp; completed status</p>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="import-spreadsheet-item" onClick={() => setShowBulkImport(true)} className="flex items-start gap-3 py-2.5 cursor-pointer">
                  <FileSpreadsheet className="w-4 h-4 mt-0.5 text-[#002FA7]" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Spreadsheet (CSV / Excel)</p>
                    <p className="text-xs text-slate-500">Bulk template import · Amazon Relay CSV exports auto-detected</p>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="import-history-item" onClick={() => setShowImportHistory(true)} className="flex items-start gap-3 py-2.5 cursor-pointer">
                  <History className="w-4 h-4 mt-0.5 text-slate-500" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Import History</p>
                    <p className="text-xs text-slate-500">Every batch, who ran it, the source file — and undo</p>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              data-testid="create-load-button"
              onClick={() => { track('click', 'load-create-open'); setShowForm(!showForm); setError(''); }}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-[#002FA7] text-white px-3 sm:px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200 text-sm"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              <span className="hidden sm:inline">Create Load</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>
        </div>

        {showCarrierCheck && <CarrierCheckWidget onClose={() => setShowCarrierCheck(false)} />}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total', value: stats.total, color: 'text-[#002FA7]' },
            { label: 'Assigned', value: stats.assigned, color: 'text-blue-600' },
            { label: 'In Transit', value: stats.in_transit, color: 'text-[#34C759]' },
            { label: 'Delivered', value: stats.delivered, color: 'text-slate-600' },
          ].map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`} className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center gap-3 mb-2">
                <Truck className={`w-8 h-8 ${s.color}`} strokeWidth={2} />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{s.label}</p>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{s.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showForm && (
          <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>New Load</h2>

            {/* PDF auto-fill */}
            <div
              data-testid="pdf-extract-block"
              className="mb-5 rounded-lg border border-dashed border-[#002FA7]/30 bg-gradient-to-br from-[#002FA7]/[0.04] to-transparent p-5"
            >
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#002FA7]" strokeWidth={2.25} />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                    Auto-fill from rate confirmation
                  </h3>
                  <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Upload a rate-con / load-tender (PDF, JPG or PNG) and we&apos;ll pre-fill pickup, delivery, cargo, miles, notes and try to match the driver.
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf,image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                  onChange={handlePdfUpload}
                  className="hidden"
                  data-testid="pdf-extract-input"
                />
                <button
                  type="button"
                  data-testid="pdf-extract-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={extracting}
                  className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#0034BD] text-white px-4 py-2.5 rounded-md text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {extracting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />
                      Reading PDF…
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" strokeWidth={2.5} />
                      Upload PDF
                    </>
                  )}
                </button>
              </div>

              {extractError && (
                <div data-testid="pdf-extract-error" className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {extractError}
                </div>
              )}

              {extractedFields && extractedFields.length > 0 && (
                <div data-testid="pdf-extract-success" className="mt-3 flex items-start gap-2 text-xs text-[#117A33] bg-[#34C759]/10 border border-[#34C759]/30 rounded-md px-3 py-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <div>
                    <span className="font-semibold">Pre-filled {extractedFields.length} field{extractedFields.length === 1 ? '' : 's'}.</span> Please review before saving.
                    {extractedDriverName && (
                      <div className="mt-1 text-amber-700">
                        We saw &ldquo;{extractedDriverName}&rdquo; on the PDF but couldn&apos;t auto-match an active driver — please pick one below.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div data-testid="load-form-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">{error}</div>
            )}
            <form onSubmit={handleCreate} className="space-y-5">
              {/* Row 1: Load ID + Load Rate */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Load ID# <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    data-testid="load-ref-input"
                    value={form.load_ref}
                    onChange={(e) => setForm({ ...form, load_ref: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                    placeholder="TC-2026-0455"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Load Rate <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    data-testid="load-rate-input"
                    value={form.load_rate}
                    onChange={(e) => setForm({ ...form, load_rate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                    placeholder="$2,800"
                  />
                  {currentPerMile != null && (
                    <div
                      data-testid="rate-per-mile-badge"
                      className={`mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-md border text-xs font-semibold ${toneStyles[benchmarkTone]}`}
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      <span className="tabular-nums">${currentPerMile.toFixed(2)} / mi</span>
                      {benchmarkAvg != null && benchmarkPctText && (
                        <>
                          <span className="opacity-50">·</span>
                          <span className="tabular-nums">{benchmarkPctText} vs {benchmarkLabel}</span>
                        </>
                      )}
                      {benchmarkAvg == null && (
                        <>
                          <span className="opacity-50">·</span>
                          <span className="opacity-80">no benchmark yet</span>
                        </>
                      )}
                    </div>
                  )}
                  {currentPerMile == null && parsedRate != null && parsedMiles == null && (
                    <p className="mt-2 text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      Add total miles to see $/mile.
                    </p>
                  )}
                </div>
              </div>

              {/* Row 2: Broker + Driver Assignment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Load Type</label>
                  <select
                    data-testid="load-type-select"
                    value={form.load_type}
                    onChange={(e) => setForm({ ...form, load_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    <option value="dry_van">Dry Van</option>
                    <option value="reefer">Reefer</option>
                    <option value="flatbed">Flatbed</option>
                    <option value="power_only">Power Only</option>
                    <option value="drop_hook">Drop & Hook</option>
                    <option value="live">Live Load / Live Unload</option>
                    <option value="tow_away">Tow Away</option>
                    <option value="load_out">Load-Out Trailer</option>
                    <option value="dedicated">Dedicated</option>
                    <option value="intermodal">Intermodal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Tolls (optional)</label>
                  <input
                    type="number"
                    data-testid="load-tolls-input"
                    value={form.tolls}
                    onChange={(e) => setForm({ ...form, tolls: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    placeholder="$0"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              {/* Live Profitability Preview */}
              {(profitPreview || profitPreviewLoading) && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="load-create-profitability-preview">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold uppercase tracking-widest text-slate-700" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                      Live Profitability
                    </h4>
                    {profitPreviewLoading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                  </div>
                  <ProfitabilityPanel
                    metrics={profitPreview?.metrics}
                    negotiationMessage={profitPreview?.negotiation_message}
                    dense
                  />
                </div>
              )}

              {/* Row 3: Broker + Driver Assignment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Broker <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    data-testid="load-broker-input"
                    value={form.broker}
                    onChange={(e) => setForm({ ...form, broker: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                    placeholder="ABC Logistics"
                  />
                  {brokerHistory && (
                    <div
                      className={`mt-1.5 text-[11px] flex items-center gap-1.5 px-2 py-1 rounded ${
                        brokerHistory.grade === 'A+' || brokerHistory.grade === 'A' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
                        brokerHistory.grade === 'B' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                        brokerHistory.grade === 'C' ? 'bg-orange-50 text-orange-800 border border-orange-200' :
                        'bg-red-50 text-red-800 border border-red-200'
                      }`}
                      data-testid="broker-history-flag"
                    >
                      <span className={`inline-block px-1 py-0 rounded text-[10px] font-bold ${
                        brokerHistory.grade === 'A+' || brokerHistory.grade === 'A' ? 'bg-emerald-600 text-white' :
                        brokerHistory.grade === 'B' ? 'bg-amber-500 text-white' :
                        brokerHistory.grade === 'C' ? 'bg-orange-500 text-white' :
                        'bg-red-600 text-white'
                      }`}>{brokerHistory.grade}</span>
                      {brokerHistory.rollup?.avg_rpm
                        ? <>Last {brokerHistory.load_count_returned} load{brokerHistory.load_count_returned === 1 ? '' : 's'} averaged <strong>${brokerHistory.rollup.avg_rpm}/mi</strong>{brokerHistory.rollup.avg_profit !== null && <> · <strong>${Math.round(brokerHistory.rollup.avg_profit)}</strong> profit</>}{typeof brokerHistory.rollup.avg_margin_pct === 'number' && <> · {brokerHistory.rollup.avg_margin_pct.toFixed(1)}% margin</>}</>
                        : <>{brokerHistory.load_count_total} prior load{brokerHistory.load_count_total === 1 ? '' : 's'} on file</>}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Driver Assignment <span className="text-red-500">*</span></label>
                  <SearchableDriverSelect
                    drivers={drivers}
                    value={form.driver_id}
                    onChange={(uid) => setForm({ ...form, driver_id: uid })}
                    placeholder="Search active drivers by name…"
                    testid="load-driver-select"
                  />
                  {drivers.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No active drivers found. Onboard and approve drivers first.</p>
                  )}
                </div>
              </div>

              {/* Truck — auto-links from the driver's assigned truck; override when a driver has more than one */}
              {trucks.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Truck <span className="text-slate-400 normal-case tracking-normal">— optional, auto-links to the driver's truck</span>
                  </label>
                  <select
                    data-testid="load-truck-select"
                    value={form.truck_asset_id}
                    onChange={(e) => setForm({ ...form, truck_asset_id: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    <option value="">Auto — use the driver's assigned truck</option>
                    {trucks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {(t.label || t.asset_number || 'Truck')}{t.asset_number ? ` (${t.asset_number})` : ''}{t.assigned_driver_id && form.driver_id && String(t.assigned_driver_id) === String(form.driver_id) ? " · driver's truck" : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Co-driver (team) — optional second driver on the same load */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Co-driver (team) <span className="text-slate-400 normal-case tracking-normal">— optional</span>
                </label>
                <SearchableDriverSelect
                  drivers={drivers.filter((d) => d.user_id !== form.driver_id)}
                  value={form.co_driver_id}
                  onChange={(uid) => setForm({ ...form, co_driver_id: uid })}
                  placeholder="Search a second driver (team run)…"
                  testid="load-co-driver-select"
                />
                <p className="text-[11px] text-slate-500 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  The primary driver is paid the load. The co-driver's share is handled via a Pay Transfer on payroll.
                </p>
              </div>

              {/* Row 3: Pick Up Date + Delivery Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Pick Up Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    data-testid="load-pickup-date-input"
                    value={form.pickup_date}
                    onChange={(e) => setForm({ ...form, pickup_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Delivery Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    data-testid="load-delivery-date-input"
                    value={form.delivery_date}
                    onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                  />
                </div>
              </div>

              {/* Row 4: Pickup Stops (multi) */}
              <div data-testid="pickup-stops-section">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Pick Up Stop <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addPickupStop}
                    data-testid="add-pickup-stop-button"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:text-[#0034BD]"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add stop
                  </button>
                </div>
                <div className="space-y-2">
                  {(form.pickup_stops || []).map((stop, i) => (
                    <div key={`p-${i}`} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center" data-testid={`pickup-stop-row-${i}`}>
                      <input
                        type="text"
                        value={stop.city}
                        onChange={(e) => updatePickupStop(i, 'city', e.target.value)}
                        className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        placeholder={i === 0 ? 'Origin city' : `Stop ${i + 1} city`}
                        data-testid={`pickup-city-${i}`}
                      />
                      <select
                        value={stop.state}
                        onChange={(e) => updatePickupStop(i, 'state', e.target.value)}
                        className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        data-testid={`pickup-state-${i}`}
                      >
                        <option value="">State</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => removePickupStop(i)}
                        data-testid={`remove-pickup-stop-${i}`}
                        disabled={(form.pickup_stops || []).length === 1}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                        title={(form.pickup_stops || []).length === 1 ? 'At least one stop is required' : 'Remove stop'}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Row 4b: Delivery Stops (multi) */}
              <div data-testid="delivery-stops-section">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Delivery Stop <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addDeliveryStop}
                    data-testid="add-delivery-stop-button"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:text-[#0034BD]"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add stop
                  </button>
                </div>
                <div className="space-y-2">
                  {(form.delivery_stops || []).map((stop, i) => (
                    <div key={`d-${i}`} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center" data-testid={`delivery-stop-row-${i}`}>
                      <input
                        type="text"
                        value={stop.city}
                        onChange={(e) => updateDeliveryStop(i, 'city', e.target.value)}
                        className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        placeholder={i === (form.delivery_stops || []).length - 1 ? 'Final destination city' : `Stop ${i + 1} city`}
                        data-testid={`delivery-city-${i}`}
                      />
                      <select
                        value={stop.state}
                        onChange={(e) => updateDeliveryStop(i, 'state', e.target.value)}
                        className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        data-testid={`delivery-state-${i}`}
                      >
                        <option value="">State</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeDeliveryStop(i)}
                        data-testid={`remove-delivery-stop-${i}`}
                        disabled={(form.delivery_stops || []).length === 1}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                        title={(form.delivery_stops || []).length === 1 ? 'At least one stop is required' : 'Remove stop'}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Route Preview Map */}
              <div data-testid="route-map-block">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Route Preview
                  </label>
                  {orderedStopsWithCoords.filter((s) => typeof s.lat === 'number').length >= 2 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="inline-block w-2 h-2 rounded-full bg-[#002FA7]" /> Pickups
                      <span className="inline-block w-2 h-2 rounded-full bg-[#34C759] ml-2" /> Deliveries
                    </span>
                  )}
                </div>
                <RoutePreviewMap stops={orderedStopsWithCoords} breakdown={milesBreakdown} height={260} />
              </div>

              {/* Row 5: Deadhead / Loaded / Total Miles */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Deadhead Miles</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    data-testid="load-deadhead-input"
                    value={form.deadhead_miles}
                    onChange={(e) => setForm({ ...form, deadhead_miles: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    placeholder="e.g., 23"
                  />
                  {deadheadCalcState.message && (
                    <p
                      data-testid="deadhead-auto-note"
                      className={`text-[11px] mt-1 ${
                        deadheadCalcState.status === 'ok' ? 'text-[#117A33]'
                        : deadheadCalcState.status === 'warn' ? 'text-amber-700'
                        : deadheadCalcState.status === 'error' ? 'text-red-600'
                        : 'text-slate-500'
                      }`}
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {deadheadCalcState.status === 'loading' && <Loader2 className="inline w-3 h-3 animate-spin mr-1" />}
                      {deadheadCalcState.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loaded Miles <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    inputMode="numeric"
                    data-testid="load-loaded-miles-input"
                    value={form.loaded_miles}
                    onChange={(e) => setForm({ ...form, loaded_miles: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                    placeholder="e.g., 925"
                  />
                  {milesCalcState.message && (
                    <p
                      data-testid="miles-auto-note"
                      className={`text-[11px] mt-1 ${
                        milesCalcState.status === 'ok' ? 'text-[#117A33]'
                        : milesCalcState.status === 'warn' ? 'text-amber-700'
                        : milesCalcState.status === 'error' ? 'text-red-600'
                        : 'text-slate-500'
                      }`}
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {milesCalcState.status === 'loading' && <Loader2 className="inline w-3 h-3 animate-spin mr-1" />}
                      {milesCalcState.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Total Miles <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    inputMode="numeric"
                    data-testid="load-total-miles-input"
                    value={form.total_miles}
                    onChange={(e) => setForm({ ...form, total_miles: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-slate-50"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                    placeholder="auto-calculated"
                  />
                  <p className="text-[11px] text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Auto-sum of deadhead + loaded. Editable.</p>
                </div>
              </div>

              {/* Row 6: Driver Pay Type + Dispatcher */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Driver Pay Type <span className="text-red-500">*</span></label>
                  <div className="flex flex-wrap items-center gap-4" data-testid="load-pay-type-group">
                    {PAY_TYPE_OPTIONS.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 cursor-pointer" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <input
                          type="radio"
                          name="driver_pay_type"
                          value={opt.value}
                          checked={form.driver_pay_type === opt.value}
                          onChange={(e) => setForm({ ...form, driver_pay_type: e.target.value })}
                          data-testid={`load-pay-type-${opt.value}`}
                          className="accent-[#002FA7] w-4 h-4"
                        />
                        <span className="text-sm text-slate-800">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  {payTypeAutoNote && (
                    <p data-testid="pay-type-auto-note" className="text-[11px] text-slate-500 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {payTypeAutoNote}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Dispatcher</label>
                  <select
                    data-testid="load-dispatcher-select"
                    value={form.dispatcher_id}
                    onChange={(e) => setForm({ ...form, dispatcher_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    <option value="">Please Select</option>
                    {dispatchers.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.role === 'company_admin' ? 'Admin' : 'Dispatcher'})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 7: Load Status + Factoring Company */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Load Status <span className="text-red-500">*</span></label>
                  <select
                    data-testid="load-status-select"
                    value={form.load_status}
                    onChange={(e) => setForm({ ...form, load_status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                  >
                    <option value="assigned">Assigned</option>
                    <option value="in_transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Factoring Company</label>
                  <select
                    data-testid="load-factoring-select"
                    value={form.factoring_company}
                    onChange={(e) => setForm({ ...form, factoring_company: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    <option value="">Please Select</option>
                    {FACTORING_COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 8: Notes + File Upload */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Notes</label>
                  <textarea
                    data-testid="load-notes-input"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={5}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none resize-y"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    placeholder="Special instructions, appointment requirements, etc."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>File Upload</label>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    onChange={handleAttachmentSelect}
                    className="hidden"
                    data-testid="load-attachment-input"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                  {!fileAttachmentMeta ? (
                    <button
                      type="button"
                      data-testid="load-attachment-button"
                      onClick={() => attachmentInputRef.current?.click()}
                      className="w-full h-[124px] border-2 border-dashed border-slate-300 rounded-md flex flex-col items-center justify-center gap-2 hover:border-[#002FA7] hover:bg-[#002FA7]/[0.04] transition-all"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      <Upload className="w-5 h-5 text-slate-400" strokeWidth={2} />
                      <span className="text-sm font-semibold text-slate-700">Browse files</span>
                      <span className="text-xs text-slate-500">Drag and drop files here (max 10MB)</span>
                    </button>
                  ) : (
                    <div className="border border-slate-300 rounded-md p-3 flex items-center justify-between gap-3" data-testid="load-attachment-summary">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{fileAttachmentMeta.name}</p>
                        <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{fileAttachmentMeta.sizeKb} KB attached</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveAttachment}
                        data-testid="load-attachment-remove"
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setError(''); }}
                  data-testid="cancel-load-button"
                  className="px-5 py-2.5 border border-slate-300 text-slate-700 rounded-md font-semibold hover:bg-slate-50 transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-load-button"
                  className="bg-[#FF6B00] text-white px-7 py-2.5 rounded-md font-semibold hover:bg-[#E55F00] transition-all duration-200 shadow-sm shadow-[#FF6B00]/30"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>All Loads</h2>
            <select
              data-testid="status-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none text-sm"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <option value="all">All Statuses</option>
              {LOAD_STATUSES.map((st) => <option key={st} value={st}>{`${statusLabel(st)}`}</option>)}
              <option value="unlinked">{`Unlinked driver${unlinkedCount ? ` (${unlinkedCount})` : ''}`}</option>
            </select>
          </div>

          {filteredLoads.length === 0 ? (
            <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No loads found.</p>
          ) : (
            <>
              {/* Mobile-first: card layout under md breakpoint */}
              <div className="space-y-3 md:hidden" data-testid="loads-mobile-list">
                {filteredLoads.map((load) => (
                  <div
                    key={load.id}
                    data-testid={`load-card-${load.id}`}
                    className={`bg-white border rounded-lg p-4 ${highlightId === load.id ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          <MapPin className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                          <span className="truncate">{load.pickup_location}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          <span className="w-4 inline-block text-center text-slate-400">↓</span>
                          <span className="truncate">{load.delivery_location}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          {load.source === 'pdf_extraction' && (
                            <button
                              type="button"
                              onClick={() => setReviewLoadId(load.id)}
                              data-testid={`mobile-pdf-badge-${load.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#002FA7]/10 text-[#002FA7]"
                            >
                              <Sparkles className="w-3 h-3" /> PDF
                            </button>
                          )}
                          {load.stop_count > 2 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800">
                              {load.stop_count} stops
                            </span>
                          )}
                          {(load.trip_stops || []).length > 0 && (
                            <button type="button" onClick={() => setStopsLoad(load)} data-testid={`trip-stops-badge-card-${load.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-800 hover:bg-sky-200">
                              <MapPin className="w-3 h-3" /> {load.trip_stops.length} trip stop{load.trip_stops.length !== 1 ? 's' : ''}
                            </button>
                          )}
                          <MissingDocsBadge missingKinds={load.missing_doc_kinds} />
                        </div>
                      </div>
                      <LoadStatusPill status={load.status} size="xs" testId={`load-card-status-${load.id}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="col-span-2"><span className="text-slate-400">Driver:</span> <DriverCell load={load} variant="card-" /></span>
                      <span><span className="text-slate-400">Cargo:</span> {load.cargo_description || '—'}</span>
                      <span className="col-span-2"><span className="text-slate-400">Created:</span> {new Date(load.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 -mx-1">
                      <button
                        onClick={() => setChatLoad({ id: load.id, load_ref: load.load_ref || load.id.slice(-6) })}
                        data-testid={`mobile-chat-load-${load.id}`}
                        className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-semibold text-[#002FA7] bg-[#002FA7]/5 active:bg-[#002FA7]/10 rounded-md px-2"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        <MessageSquare className="w-4 h-4" /> Chat
                      </button>
                      <button
                        onClick={() => setDocsLoad(load)}
                        data-testid={`mobile-docs-load-${load.id}`}
                        className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-100 active:bg-slate-200 rounded-md px-2"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        <FileBadge2 className="w-4 h-4" /> Docs
                      </button>
                      <button
                        onClick={() => setPlaybackLoad(load)}
                        data-testid={`mobile-playback-load-${load.id}`}
                        className="min-h-[44px] flex items-center justify-center text-slate-700 bg-slate-100 active:bg-slate-200 rounded-md px-3"
                        title="Trip playback"
                      >
                        <Route className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditLoad(load)}
                        data-testid={`mobile-edit-load-${load.id}`}
                        className={`flex-1 min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-semibold rounded-md px-2 ${load.status === 'delivered' ? 'text-[#FF6B00] bg-orange-50 active:bg-orange-100' : 'text-white bg-[#002FA7] active:bg-[#00247D]'}`}
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        {load.status === 'delivered' ? <RefreshCw className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                        {load.status === 'delivered' ? 'Reopen' : 'Edit'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: full table */}
              <div className="overflow-x-auto hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Route</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Driver</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Profitability</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Status</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Created</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLoads.map((load) => (
                    <tr key={load.id} data-testid={`load-row-${load.id}`} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${highlightId === load.id ? 'bg-amber-50 ring-1 ring-amber-300' : ''}`}>
                      <td className="py-3 px-2 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#002FA7]" />
                          <span>{load.pickup_location} → {load.delivery_location}</span>
                          {load.source === 'pdf_extraction' && (
                            <button
                              type="button"
                              onClick={() => setReviewLoadId(load.id)}
                              title="Created from rate confirmation PDF — click to view original"
                              data-testid={`pdf-badge-${load.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7]/20"
                            >
                              <Sparkles className="w-3 h-3" /> PDF
                            </button>
                          )}
                          {load.load_ref && /-DUP\d+$/.test(load.load_ref) && (
                            <span
                              title="Possible duplicate — saved with a -DUPn suffix when an existing load_ref already existed"
                              data-testid={`dup-badge-${load.id}`}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800"
                            >
                              POSSIBLE DUP
                            </span>
                          )}
                          {load.is_demo && (
                            <span
                              title="Sample data — demo tenant"
                              data-testid={`demo-badge-${load.id}`}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
                            >
                              DEMO
                            </span>
                          )}
                          {load.stop_count > 2 && (
                            <span title={`${load.stop_count} stops`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800">
                              {load.stop_count} stops
                            </span>
                          )}
                          {(load.trip_stops || []).length > 0 && (
                            <button type="button" onClick={() => setStopsLoad(load)} data-testid={`trip-stops-badge-${load.id}`}
                              title="View fuel / scale / repair stops logged by the driver"
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-800 hover:bg-sky-200">
                              <MapPin className="w-3 h-3" /> {load.trip_stops.length} trip stop{load.trip_stops.length !== 1 ? 's' : ''}
                            </button>
                          )}
                          <MissingDocsBadge missingKinds={load.missing_doc_kinds} />
                        </div>
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}><DriverCell load={load} /></td>
                      <td className="py-3 px-2" data-testid={`load-profitability-${load.id}`}>
                        {load.profitability ? (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              <ProfitabilityPill metrics={load.profitability} />
                              <BookingScoreChip metrics={load.profitability} />
                            </div>
                            <div className="text-[11px] text-slate-600 tabular-nums">
                              <span className="font-semibold">${(Number(load.profitability.rpm) || 0).toFixed(2)}/mi</span>
                              <span className="mx-1 text-slate-400">·</span>
                              <span className={(Number(load.profitability.profit) || 0) >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                                ${Math.round(Number(load.profitability.profit) || 0).toLocaleString()}
                              </span>
                              <span className="text-slate-400 ml-1">({(Number(load.profitability.margin_pct) || 0).toFixed(0)}%)</span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">no data</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <LoadStatusPill status={load.status} testId={`load-status-${load.id}`} />
                        {['cancelled', 'rejected', 'recovered'].includes(load.status) && (load.status_history || []).slice(-1)[0]?.reason && (
                          <p className="text-[10px] text-slate-500 mt-1 max-w-[160px] truncate" title={(load.status_history || []).slice(-1)[0].reason} data-testid={`load-status-reason-${load.id}`}>
                            {(load.status_history || []).slice(-1)[0].reason}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{new Date(load.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`chat-load-${load.id}`}
                            onClick={() => setChatLoad({ id: load.id, load_ref: load.load_ref || load.id.slice(-6) })}
                            title="Open chat"
                            className="text-slate-500 hover:text-[#002FA7]"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`docs-load-${load.id}`}
                            onClick={() => setDocsLoad(load)}
                            title="Documents (POD, BOL, Lumper, Scale Ticket)"
                            className="text-slate-500 hover:text-[#002FA7]"
                          >
                            <FileBadge2 className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`playback-load-${load.id}`}
                            onClick={() => setPlaybackLoad(load)}
                            title="Trip playback — replay the driver's GPS trail"
                            className="text-slate-500 hover:text-[#002FA7]"
                          >
                            <Route className="w-4 h-4" />
                          </button>
                          {load.status !== 'cancelled' && (
                            <ShareEtaButton loadId={load.id} shareToken={load.share_token} compact
                                            onChange={(t) => setLoads((prev) => prev.map((l) => (l.id === load.id ? { ...l, share_token: t } : l)))} />
                          )}
                          <button
                            data-testid={`edit-load-${load.id}`}
                            onClick={() => setEditLoad(load)}
                            title={load.status === 'delivered' ? 'Reopen / correct completed load' : 'Edit status / reassign'}
                            className={`hover:text-[#00247D] ${load.status === 'delivered' ? 'text-[#FF6B00]' : 'text-[#002FA7]'}`}
                          >
                            {load.status === 'delivered' ? <RefreshCw className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                          </button>
                          {(user?.role === 'company_admin' || user?.role === 'super_admin') && (
                            <button
                              data-testid={`delete-load-${load.id}`}
                              onClick={() => handleDelete(load.id)}
                              title="Delete load"
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>
      {/* Sticky FAB — mobile only, opens the new-load form */}
      {!showForm && (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="mobile-new-load-fab"
          className="lg:hidden fixed right-4 bottom-20 z-30 w-14 h-14 rounded-full text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          style={{ background: 'var(--brand-primary, #002FA7)' }}
          aria-label="Add new load"
        >
          <Plus className="w-6 h-6" strokeWidth={2.5} />
        </button>
      )}
      <LoadChatDrawer
        loadId={chatLoad?.id}
        loadRef={chatLoad?.load_ref}
        open={!!chatLoad}
        onClose={() => setChatLoad(null)}
      />
      {stopsLoad && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="trip-stops-modal" onClick={() => setStopsLoad(null)}>
          <div className="bg-white rounded-lg w-full max-w-lg shadow-xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 shrink-0">
              <div>
                <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Trip Stops</h3>
                <p className="text-xs text-slate-500">{stopsLoad.pickup_location} → {stopsLoad.delivery_location}</p>
              </div>
              <button data-testid="trip-stops-close" onClick={() => setStopsLoad(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 overflow-y-auto"><LoadStopsMap stops={stopsLoad.trip_stops} height={240} loadId={stopsLoad.id} /></div>
          </div>
        </div>
      )}
      {playbackLoad && <TripPlaybackModal load={playbackLoad} onClose={() => setPlaybackLoad(null)} />}
      {linkLoad && <LinkDriverModal load={linkLoad} onClose={() => setLinkLoad(null)} onLinked={fetchData} />}
      <LoadEditModal
        load={editLoad}
        drivers={drivers}
        role={user?.role}
        open={!!editLoad}
        onClose={() => setEditLoad(null)}
        onUpdated={fetchData}
      />
      <LoadDocumentsModal
        load={docsLoad}
        role={user?.role}
        currentUserId={user?.id}
        open={!!docsLoad}
        onClose={() => setDocsLoad(null)}
      />
      <LoadReviewModal
        open={showReview || !!reviewLoadId}
        viewLoadId={reviewLoadId}
        drivers={drivers}
        onClose={() => { setShowReview(false); setReviewLoadId(null); }}
        onCreated={(created) => {
          setShowReview(false);
          setReviewLoadId(null);
          if (created?._duplicate_suffix_applied) {
            toast.warning('Existing load_ref already in use', {
              description: `New load saved as "${created._duplicate_suffix_applied}".`,
              duration: 6000,
            });
          }
          fetchData();
        }}
      />
      <BulkLoadImportModal
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        drivers={drivers}
        onDone={() => fetchData()}
        onAmazonDetected={(file) => { setShowBulkImport(false); setMultiImportFile(file); setShowMultiImport(true); }}
      />
      <MultiLoadImportModal
        open={showMultiImport}
        initialFile={multiImportFile}
        drivers={drivers}
        onClose={() => { setShowMultiImport(false); setMultiImportFile(null); }}
        onDone={() => fetchData()}
      />
      <ImportHistoryModal
        open={showImportHistory}
        onClose={() => setShowImportHistory(false)}
        onUndone={() => fetchData()}
      />
    </DashboardLayout>
  );
};

export default LoadsManagement;
